import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { toast } from '@flash-global66/g-toast';
import { currencyConfigFormat } from '@/helpers/formatCurrency';
import { currencyToCountryCode } from '@/helpers/currencyToCountryCode';
import { getReasonMessage } from '@/shared/common';
import { firstRouteValue } from '@/shared/common/utils/routeValue.util';
import { findFirstMovementIdByChannel } from '@/modules/cashIn/utils/productGatewayMovement.util';
import type { PaymentStatusType } from '@/modules/cashIn/types/paymentStatusView.type';
import type { InswitchB2bTransactionResponse } from '@/shared/inswitch/types/inswitch.type';
import { cashInService } from '@/modules/cashIn/services/cashIn.service';
import { inswitchService } from '@/shared/inswitch/services/inswitch.service';
import {
  INSWITCH_SESSION_KEYS,
  INSWITCH_B2B_TRANSACTION_STATUS,
  INSWITCH_PARTNER_NAME,
  INSWITCH_PAYMENT_UI_STATUS,
  INSWITCH_CALLBACK_OUTCOME,
  INSWITCH_ALLOWED_BY_CURRENCY,
} from '@/shared/inswitch/constants/inswitch.constant';
import { PAYMENT_STATUS_ERROR_TOAST_DEFAULT } from '@/shared/inswitch/constants/paymentStatus.constant';
import { clearInswitchSession } from '@/shared/inswitch/utils/inswitchSession.util';

/**
 * Composable para la vista de estado InSwitch tras el retorno de la pasarela (éxito, error o fallo de conexión).
 *
 * Obtiene el estado con `GET inswitch/b2b/transactions/{reference}` (referencia desde query o `sessionStorage`).
 * El segmento `route.params.outcome` solo declara un fallo explícito (`'error'`); la API es la única autoridad
 * para confirmar éxito, y ese outcome únicamente reclasifica un estado no confirmable como error en vez de
 * `connectionError`, suprimiendo el toast redundante. La limpieza de `sessionStorage` ocurre al desmontar.
 * El número de transacción mostrado prioriza el movimiento en product-gateway con canal `INSWITCH`; si no hay
 * coincidencia, si la consulta falla o si la transacción no trae moneda, usa la referencia B2B (mismo criterio que Wompi).
 * El monto mostrado es el neto a abonar (`finalAmount` de `GET route/b2b/fee-quote`), no el total cobrado por la
 * pasarela. Si la cotización no se puede resolver, el monto queda vacío y la pantalla omite esa línea en lugar
 * de mostrar el bruto.
 *
 * @returns Estado de UI (`loading` | `success` | `error` | `connectionError`), número, monto neto formateado, y manejadores de navegación que limpian `sessionStorage`.
 */
export function useInswitchStatus() {
  const router = useRouter();
  const route = useRoute();

  const transactionNumber = ref('');
  const formattedAmount = ref('');
  const isLoadingStatus = ref(true);
  const realStatus = ref<PaymentStatusType | null>(null);

  const getTransactionReference = (): string | null => {
    const fromQuery = [
      firstRouteValue(route.query.transactionReference),
      firstRouteValue(route.query.referenceCode),
    ].find((s) => s !== '');
    if (fromQuery) return fromQuery;
    const stored = sessionStorage.getItem(INSWITCH_SESSION_KEYS.TRANSACTION_REFERENCE);
    return stored && stored.trim() !== '' ? stored.trim() : null;
  };

  const hasDeclaredFailure = computed(
    () => firstRouteValue(route.params.outcome) === INSWITCH_CALLBACK_OUTCOME.ERROR
  );

  const status = computed<PaymentStatusType>(() => {
    if (isLoadingStatus.value) return 'loading';
    if (realStatus.value) return realStatus.value;
    return 'error';
  });

  function getTransactionCurrency(data: InswitchB2bTransactionResponse): string {
    return String(data.currency ?? '').trim();
  }

  function applyTransactionReferenceAsNumber(data: InswitchB2bTransactionResponse): void {
    const fallback = data.transactionReference;
    if (fallback && String(fallback).trim() !== '') {
      transactionNumber.value = String(fallback).trim();
    }
  }

  async function fetchNetAmountToCredit(data: InswitchB2bTransactionResponse): Promise<void> {
    const currencyCode = getTransactionCurrency(data).toUpperCase();
    const grossAmount = Number(data.amount);

    if (!INSWITCH_ALLOWED_BY_CURRENCY.includes(currencyCode)) return;
    if (!Number.isFinite(grossAmount) || grossAmount <= 0) return;

    const countryCode = currencyToCountryCode(currencyCode);
    if (!countryCode) return;

    const result = await inswitchService.getInswitchFeeQuote({
      partnerName: INSWITCH_PARTNER_NAME,
      amount: grossAmount,
      countryCode,
      currencyCode,
    });

    result.fold(
      () => {},
      (success) => {
        const netAmount = Number(success.data?.finalAmount);
        if (!Number.isFinite(netAmount) || netAmount <= 0) return;

        formattedAmount.value = currencyConfigFormat({
          amount: netAmount,
          currencyCode,
          hasCode: true,
          hasSymbol: true,
        });
      }
    );
  }

  async function fetchInswitchMovementTransactionId(
    data: InswitchB2bTransactionResponse
  ): Promise<void> {
    const currencyCode = getTransactionCurrency(data);

    if (currencyCode === '') {
      applyTransactionReferenceAsNumber(data);
      return;
    }

    const result = await cashInService.getProductGatewayMovementsFirstPage({
      currencyCode,
    });

    result.fold(
      () => applyTransactionReferenceAsNumber(data),
      (success) => {
        const id = findFirstMovementIdByChannel(
          success.data?.movements,
          INSWITCH_PARTNER_NAME,
          { commentFallback: INSWITCH_PARTNER_NAME }
        );
        if (id !== undefined) {
          transactionNumber.value = String(id);
          return;
        }
        applyTransactionReferenceAsNumber(data);
      }
    );
  }

  async function fetchTransactionStatus(): Promise<void> {
    isLoadingStatus.value = true;
    realStatus.value = null;
    transactionNumber.value = '';
    formattedAmount.value = '';

    const reference = getTransactionReference();

    if (!reference) {
      if (!hasDeclaredFailure.value) {
        toast.error(PAYMENT_STATUS_ERROR_TOAST_DEFAULT);
      }
      realStatus.value = INSWITCH_PAYMENT_UI_STATUS.ERROR;
      isLoadingStatus.value = false;
      return;
    }

    const result = await cashInService.getInswitchB2bTransactionByReference(reference);

    await result.foldAsync(
      async (failure) => {
        if (hasDeclaredFailure.value) {
          realStatus.value = INSWITCH_PAYMENT_UI_STATUS.ERROR;
          isLoadingStatus.value = false;
          return;
        }
        const errorMessage = getReasonMessage({
          failure,
          defaultMessage: PAYMENT_STATUS_ERROR_TOAST_DEFAULT,
        });
        toast.error(errorMessage);
        realStatus.value = INSWITCH_PAYMENT_UI_STATUS.CONNECTION_ERROR;
        isLoadingStatus.value = false;
      },
      async (success) => {
        const data = success.data;

        if (!data || typeof data !== 'object') {
          if (!hasDeclaredFailure.value) {
            toast.error(PAYMENT_STATUS_ERROR_TOAST_DEFAULT);
          }
          realStatus.value = INSWITCH_PAYMENT_UI_STATUS.ERROR;
          isLoadingStatus.value = false;
          return;
        }

        const rawStatus = data.transactionStatus;
        if (rawStatus === undefined || rawStatus === null || String(rawStatus).trim() === '') {
          if (!hasDeclaredFailure.value) {
            toast.error(PAYMENT_STATUS_ERROR_TOAST_DEFAULT);
          }
          realStatus.value = INSWITCH_PAYMENT_UI_STATUS.ERROR;
          isLoadingStatus.value = false;
          return;
        }

        const normalized = String(rawStatus).toLowerCase();

        let mapped: PaymentStatusType;
        if (normalized === INSWITCH_B2B_TRANSACTION_STATUS.FINISHED) {
          mapped = INSWITCH_PAYMENT_UI_STATUS.SUCCESS;
        } else if (normalized === INSWITCH_B2B_TRANSACTION_STATUS.CANCELLED) {
          mapped = INSWITCH_PAYMENT_UI_STATUS.ERROR;
        } else {
          mapped = INSWITCH_PAYMENT_UI_STATUS.ERROR;
        }

        realStatus.value = mapped;

        const pendingLookups = [fetchInswitchMovementTransactionId(data)];
        if (mapped === INSWITCH_PAYMENT_UI_STATUS.SUCCESS) {
          pendingLookups.push(fetchNetAmountToCredit(data));
        }
        await Promise.all(pendingLookups);

        isLoadingStatus.value = false;
      }
    );
  }

  function handleGoHome(): void {
    clearInswitchSession();
    router.push({ name: 'movements' });
  }

  function handleTryAgain(): void {
    clearInswitchSession();
    router.push({ name: 'recharge-money' });
  }

  onMounted(() => {
    fetchTransactionStatus();
  });

  onUnmounted(clearInswitchSession);

  return {
    status,
    transactionNumber,
    formattedAmount,
    handleGoHome,
    handleTryAgain,
  };
}
